/**
 * Background Carousel Component
 * Displays a carousel of background images with smooth transitions
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { BackgroundCarouselProps } from '@/types';
import { BACKGROUND_TRANSITION_DURATION } from '@/utils/constants';

const MOBILE_BREAKPOINT = 768;

export function BackgroundCarousel({
  images,
  transitionDuration = BACKGROUND_TRANSITION_DURATION,
}: BackgroundCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, transitionDuration);

    return () => clearInterval(interval);
  }, [images.length, transitionDuration]);

  // Reset index when images change
  useEffect(() => {
    setCurrentIndex(0);
  }, [images]);

  // Show default gradient if no images
  if (!images || images.length === 0) {
    return (
      <div className="fixed inset-0 min-h-[100dvh] bg-gradient-to-br from-nomade-primary via-gray-900 to-black" />
    );
  }

  const currentImage = images[currentIndex];

  return (
    <div className="fixed inset-0 min-h-[100dvh] overflow-hidden">
      {/* Image Carousel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImage}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.5, ease: 'easeInOut' }}
          className="absolute inset-0 min-h-[100dvh]"
        >
          <div
            className={`h-full min-h-[100dvh] w-full bg-cover bg-no-repeat ${isMobile && currentIndex === 0 ? 'bg-left' : 'bg-center'}`}
            style={{
              backgroundImage: `url(${currentImage})`,
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Dark overlay for text contrast */}
      <div className="absolute inset-0 min-h-[100dvh] bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

      {/* Vignette effect */}
      <div className="absolute inset-0 min-h-[100dvh] bg-gradient-radial from-transparent via-transparent to-black/50" />
    </div>
  );
}
